import { useMemo, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { Star, ArrowRight, ArrowDown } from "lucide-react";
import { IMAGES, PROJECTS } from "@/data/site";
import { useLead } from "@/App";
import { MaskedLines, Magnetic, EASE } from "./motion";

const CHIPS = [
  { icon: Star, label: "5★ Google Rating" },
  { label: "100% Customized" },
  { label: "Premium Materials" },
  { label: "Transparent Pricing" },
];

function Particles() {
  const dots = useMemo(
    () =>
      Array.from({ length: 26 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        top: Math.random() * 100,
        size: 2 + Math.random() * 3,
        dur: 5 + Math.random() * 7,
        delay: Math.random() * 5,
      })),
    []
  );
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((d) => (
        <motion.span
          key={d.id}
          className="absolute rounded-full bg-[#B68D40]"
          style={{ left: `${d.left}%`, top: `${d.top}%`, width: d.size, height: d.size }}
          animate={{ y: [0, -60, 0], opacity: [0, 0.8, 0] }}
          transition={{ duration: d.dur, repeat: Infinity, delay: d.delay, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

export default function Hero() {
  const { openConsult } = useLead();
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 60, damping: 20 });
  const sy = useSpring(my, { stiffness: 60, damping: 20 });

  const card1x = useTransform(sx, (v) => v * 26);
  const card1y = useTransform(sy, (v) => v * 18);
  const card2x = useTransform(sx, (v) => v * -20);
  const card2y = useTransform(sy, (v) => v * -14);
  const chipX = useTransform(sx, (v) => v * 12);

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);

  const onMouseMove = (e) => {
    const { innerWidth, innerHeight } = window;
    mx.set(e.clientX / innerWidth - 0.5);
    my.set(e.clientY / innerHeight - 0.5);
  };

  const scrollToProjects = (e) => {
    e.preventDefault();
    window.__lenis?.scrollTo("#projects", { offset: -70, duration: 1.6 });
  };

  return (
    <section
      id="home"
      ref={ref}
      onMouseMove={onMouseMove}
      data-testid="hero-section"
      className="grain relative flex min-h-screen items-center overflow-hidden bg-[#1B1B1B]"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <motion.img
          src={IMAGES.hero}
          alt="Luxury living room interior by Bengaluru Interiors"
          className="h-[115%] w-full object-cover"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 2, ease: EASE, delay: 2.1 }}
        />
        <div className="absolute inset-0 bg-black/45" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(27,27,27,0.55)_100%)]" />
      </motion.div>

      <Particles />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 pb-28 pt-40 md:px-12 lg:grid-cols-[1.25fr_1fr] lg:pb-36">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 2.5 }}
            className="mb-6 flex items-center gap-3 font-num text-[11px] uppercase tracking-[0.4em] text-[#B68D40]"
          >
            <span className="h-px w-10 bg-[#B68D40]" />
            Luxury Crafted Around Your Lifestyle
          </motion.p>

          <MaskedLines
            delay={2.6}
            className="font-display text-[#F7F5F2] text-5xl md:text-6xl lg:text-7xl leading-[1.06] tracking-tight"
            lines={[
              <>Crafting <span className="italic text-[#B68D40]">Luxury</span> Interiors</>,
              <>That Feel Like <span className="italic">Home</span></>,
            ]}
          />

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 3.2 }}
            className="mt-8 max-w-xl text-base leading-relaxed text-[#F7F5F2]/75 md:text-lg"
          >
            From elegant apartments to stunning villas, we transform spaces into
            timeless experiences — designed for living, built to inspire.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 3.4 }}
            className="mt-10 flex flex-wrap items-center gap-5"
          >
            <Magnetic>
              <button
                onClick={() => openConsult("hero")}
                data-testid="hero-book-consultation-btn"
                className="group flex items-center gap-3 rounded-full bg-[#B68D40] px-8 py-4 text-sm font-semibold uppercase tracking-widest text-[#1B1B1B] transition-colors duration-300 hover:bg-[#F7F5F2]"
              >
                Book Free Consultation
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </Magnetic>
            <Magnetic>
              <a
                href="#projects"
                onClick={scrollToProjects}
                data-testid="hero-view-projects-btn"
                className="rounded-full border border-[#F7F5F2]/40 px-8 py-4 text-sm font-semibold uppercase tracking-widest text-[#F7F5F2] transition-colors duration-300 hover:border-[#B68D40] hover:text-[#B68D40]"
              >
                View Our Projects
              </a>
            </Magnetic>
          </motion.div>
        </div>

        <div className="relative hidden h-[480px] lg:block">
          <motion.div
            style={{ x: card1x, y: card1y }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 3.1 }}
            className="glass-dark absolute right-4 top-0 w-64 overflow-hidden rounded-2xl border border-white/15 shadow-[0_24px_60px_rgba(0,0,0,0.4)]"
            data-testid="hero-floating-card-1"
          >
            <img src={PROJECTS[2].img} alt={PROJECTS[2].title} className="h-36 w-full object-cover" loading="lazy" />
            <div className="p-4">
              <p className="font-display text-sm text-[#F7F5F2]">{PROJECTS[2].title}</p>
              <p className="mt-1 font-num text-[10px] uppercase tracking-[0.25em] text-[#B68D40]">{PROJECTS[2].loc}</p>
            </div>
          </motion.div>

          <motion.div
            style={{ x: card2x, y: card2y }}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, ease: EASE, delay: 3.3 }}
            className="glass-dark absolute bottom-24 left-0 w-60 overflow-hidden rounded-2xl border border-white/15 shadow-[0_24px_60px_rgba(0,0,0,0.4)]"
            data-testid="hero-floating-card-2"
          >
            <img src={PROJECTS[3].img} alt={PROJECTS[3].title} className="h-32 w-full object-cover" loading="lazy" />
            <div className="p-4">
              <p className="font-display text-sm text-[#F7F5F2]">{PROJECTS[3].title}</p>
              <p className="mt-1 font-num text-[10px] uppercase tracking-[0.25em] text-[#B68D40]">{PROJECTS[3].loc}</p>
            </div>
          </motion.div>

          <motion.div
            style={{ x: chipX }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, ease: EASE, delay: 3.5 }}
            className="animate-float glass-dark absolute bottom-0 right-8 flex items-center gap-3 rounded-full border border-white/15 px-5 py-3"
            data-testid="hero-rating-chip"
          >
            <Star size={16} className="fill-[#B68D40] text-[#B68D40]" />
            <div>
              <p className="font-num text-sm font-semibold text-[#F7F5F2]">5.0</p>
              <p className="text-[10px] uppercase tracking-widest text-[#F7F5F2]/60">Google Rating</p>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE, delay: 3.7 }}
        className="absolute inset-x-0 bottom-0 z-10 border-t border-white/10"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-6 py-5 md:px-12">
          <div className="flex flex-wrap gap-x-8 gap-y-2">
            {CHIPS.map((c, i) => (
              <span key={i} data-testid={`hero-chip-${i}`} className="flex items-center gap-2 text-xs tracking-wide text-[#F7F5F2]/75">
                {c.icon ? <c.icon size={13} className="fill-[#B68D40] text-[#B68D40]" /> : <span className="h-1 w-1 rounded-full bg-[#B68D40]" />}
                {c.label}
              </span>
            ))}
          </div>
          <a href="#services" onClick={(e) => { e.preventDefault(); window.__lenis?.scrollTo("#stats", { duration: 1.4 }); }} data-testid="hero-scroll-indicator" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#F7F5F2]/50">
            Scroll <ArrowDown size={13} className="animate-bounce" />
          </a>
        </div>
      </motion.div>
    </section>
  );
}
